<template>
    <div>
        <v-row>
            <v-col cols="12" class="d-flex align-center justify-space-between pt-0">
                <h1 class="text-h4 mb-2">Alíquota do Imposto</h1>
            </v-col>
        </v-row>

        <v-alert v-if="error" type="error" closable class="mb-4">
            {{ error }}
        </v-alert>

        <v-progress-linear v-if="storeLoading" indeterminate color="primary"></v-progress-linear>

        <v-card>
            <v-card-text>
                <v-form @submit.prevent="salvar">
                    <div class="w-50">

                        <!-- Alíquota PP -->
                        <text-field-money v-model="aliqPPStr" @raw="onMoneyRawPP" label="Alíquota do Imposto (PP)"
                            :value="aliqPPStr" prefix="R$" :disabled="saving || storeLoading"></text-field-money>

                        <!-- Alíquota PS -->
                        <text-field-money v-model="aliqPSStr" :value="aliqPSStr" @raw="onMoneyRawPS"
                            label="Alíquota do Imposto (PS)" prefix="R$" :disabled="saving || storeLoading"
                            class="mt-4"></text-field-money>

                        <div class="d-flex mt-4">
                            <v-btn color="grey-darken-1" variant="text" class="mr-2" @click="resetar"
                                :disabled="saving">
                                <v-icon left>mdi-restart</v-icon>
                            </v-btn>
                            <v-btn color="primary" type="submit" :loading="saving" :disabled="saving">
                                Salvar
                            </v-btn>
                        </div>
                    </div>
                </v-form>
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useNotificationStore } from '@/stores/notification';
import { useApi } from '@/utils/axios'
import { FINANCEIRO_ROTAS } from '@/utils/apiRoutes';
import TextFieldMoney from '@/components/inputs/TextFieldMoney.vue';

// Initialize stores
const notificationStore = useNotificationStore();

// Display strings
const aliqPPStr = ref('0,00');
const aliqPSStr = ref('0,00');

// digits as cents (strings). ex: "123456" => 1.234,56
const digitsPP = ref('');
const digitsPS = ref('');

// originals from backend (string with dot decimal, ex "13.69")
const originalPP = ref('');
const originalPS = ref('');

const saving = ref(false);
const storeLoading = ref(false);
const error = ref('');

onMounted(async () => {
    await carregar();
});

const formatFromDigits = (d) => {
    if (!d || d.length === 0) return '0,00';
    const decPart = d.slice(-2).padStart(2, '0');
    const intPart = d.slice(0, -2) || '0';
    const intWithSep = String(intPart).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${intWithSep},${decPart}`;
};

const carregar = async () => {
    try {
        storeLoading.value = true;

        const response = await useApi(FINANCEIRO_ROTAS.ALICOTAS).fetch();

        const data = response?.data || {};

        const pp = data.alicota_pp ?? '';
        const ps = data.alicota_ps ?? '';

        if ((pp !== null && pp !== '') || (ps !== null && ps !== '')) {
            originalPP.value = pp || '';
            originalPS.value = ps || '';

            // normalize backend values and convert to cents
            const numPP = Number(String(pp).replace(',', '.'));
            const centsPP = Number.isFinite(numPP) ? String(Math.round(numPP * 100)) : '';
            digitsPP.value = centsPP;
            aliqPPStr.value = formatFromDigits(digitsPP.value);

            const numPS = Number(String(ps).replace(',', '.'));
            const centsPS = Number.isFinite(numPS) ? String(Math.round(numPS * 100)) : '';
            digitsPS.value = centsPS;
            aliqPSStr.value = formatFromDigits(digitsPS.value);
        } else {
            originalPP.value = '';
            originalPS.value = '';
            digitsPP.value = '';
            digitsPS.value = '';
            aliqPPStr.value = '0,00';
            aliqPSStr.value = '0,00';
        }
    } catch {
        error.value = 'Erro ao carregar as alíquotas do imposto.';
        notificationStore.showNotification('Erro ao carregar alíquotas do imposto', 'error');

        originalPP.value = '';
        originalPS.value = '';
        digitsPP.value = '';
        digitsPS.value = '';
        aliqPPStr.value = '0,00';
        aliqPSStr.value = '0,00';
    } finally {
        storeLoading.value = false;
    }
};

const resetar = () => {
    if (originalPP.value) {
        const num = Number(String(originalPP.value).replace(',', '.'));
        digitsPP.value = Number.isFinite(num) ? String(Math.round(num * 100)) : '';
        aliqPPStr.value = formatFromDigits(digitsPP.value);
    } else {
        digitsPP.value = '';
        aliqPPStr.value = '0,00';
    }

    if (originalPS.value) {
        const num = Number(String(originalPS.value).replace(',', '.'));
        digitsPS.value = Number.isFinite(num) ? String(Math.round(num * 100)) : '';
        aliqPSStr.value = formatFromDigits(digitsPS.value);
    } else {
        digitsPS.value = '';
        aliqPSStr.value = '0,00';
    }
};

const onMoneyRawPP = (raw = '') => {
    // raw => string like "1234.56"
    const num = Number(String(raw).replace(',', '.'));
    digitsPP.value = Number.isFinite(num) ? String(Math.round(num * 100)) : '';
};

const onMoneyRawPS = (raw = '') => {
    const num = Number(String(raw).replace(',', '.'));
    digitsPS.value = Number.isFinite(num) ? String(Math.round(num * 100)) : '';
};

const salvar = async () => {
    try {
        // validação simples
        if ((!digitsPP.value || digitsPP.value.length === 0) && (!digitsPS.value || digitsPS.value.length === 0)) {
            notificationStore.showNotification('Informe ao menos uma das alíquotas', 'error');
            return;
        }

        // converte digits -> number com 2 casas decimais
        const ppNum = digitsPP.value && digitsPP.value.length > 0 ? parseFloat((Number(digitsPP.value) / 100).toFixed(2)) : 0;
        const psNum = digitsPS.value && digitsPS.value.length > 0 ? parseFloat((Number(digitsPS.value) / 100).toFixed(2)) : 0;

        saving.value = true;
        const response = await useApi(FINANCEIRO_ROTAS.ALICOTAS).put({
            alicota_pp: ppNum,
            alicota_ps: psNum
        });

        if (response && (response.success || !response.error)) {
            notificationStore.showNotification('Alíquotas salvas com sucesso!', 'success');
            // store original as backend format with dot
            originalPP.value = String(ppNum).replace(',', '.');
            originalPS.value = String(psNum).replace(',', '.');
            error.value = '';
        } else {
            notificationStore.showNotification(response?.message || 'Erro ao salvar alíquotas', 'error');
        }
    } catch (err) {
        notificationStore.showNotification(err?.response?.data?.message || 'Erro ao salvar alíquotas', 'error');
        error.value = err?.response?.data?.message || 'Erro ao salvar alíquotas';
    } finally {
        saving.value = false;
    }
};
</script>

<style scoped>
/* Consistent styling with other views */
.v-expansion-panel-title {
    padding: 10px 15px;
}

.action-icon {
    margin-left: 6px;
    cursor: pointer;
    opacity: 0.8;
    transition: opacity 0.2s, transform 0.2s;
}

.action-icon:hover {
    opacity: 1;
    transform: scale(1.2);
}
</style>